import type { Question } from '../../types';
import { HAB_MH_CONDITION } from './shared-options';

// ---------------------------------------------------------------------------
// Cyber Liability & Crime / Fidelity Supplement
// Source: Centered Partners MHMF intake — "Cyber & Crime" sheet.
// PMS systems, online rent payment, and resident PII create real cyber and
// social-engineering exposure even for small operators.
// ---------------------------------------------------------------------------

const CYBER_CONDITION = [
  { field: 'coverage_lines', operator: 'contains' as const, value: 'cyber_crime' },
  ...HAB_MH_CONDITION,
];

export const cyberHabitationalQuestions: Question[] = [
  {
    id: 'cyb_pms',
    text: 'Which property management system (PMS) do you use?',
    section: 'cyber',
    group: 'cyber_crime',
    sort_order: 500,
    field_type: 'select',
    options: [
      { label: 'Yardi', value: 'yardi' },
      { label: 'RealPage', value: 'realpage' },
      { label: 'AppFolio', value: 'appfolio' },
      { label: 'Buildium', value: 'buildium' },
      { label: 'Rent Manager', value: 'rent_manager' },
      { label: 'Other', value: 'other' },
      { label: 'None', value: 'none' },
    ],
    required: true,
    field_path: 'cyber.pms',
    conditions: CYBER_CONDITION,
    tags: ['cyber', 'lob'],
  },
  {
    id: 'cyb_pms_other',
    text: 'Which system?',
    section: 'cyber',
    group: 'cyber_crime',
    sort_order: 510,
    field_type: 'text',
    required: true,
    field_path: 'cyber.pms_other',
    conditions: [...CYBER_CONDITION, { field: 'cyb_pms', operator: 'eq', value: 'other' }],
    tags: ['cyber', 'lob'],
  },
  {
    id: 'cyb_rent_mix',
    text: 'How do residents pay rent?',
    subtext: 'Select all that apply.',
    section: 'cyber',
    group: 'cyber_crime',
    sort_order: 520,
    field_type: 'multiselect',
    options: [
      { label: 'Online portal / ACH', value: 'online' },
      { label: 'Check / money order', value: 'check' },
      { label: 'Cash', value: 'cash' },
    ],
    required: true,
    field_path: 'cyber.rent_collection_mix',
    conditions: CYBER_CONDITION,
    tags: ['cyber', 'lob'],
  },
  {
    id: 'cyb_online_volume',
    text: 'Monthly online rent payment volume',
    section: 'cyber',
    group: 'cyber_crime',
    sort_order: 530,
    field_type: 'currency',
    placeholder: '150000',
    required: true,
    field_path: 'cyber.online_rent_volume',
    conditions: [
      ...CYBER_CONDITION,
      { field: 'cyb_rent_mix', operator: 'contains', value: 'online' },
    ],
    tags: ['cyber', 'lob'],
  },
  {
    id: 'cyb_mfa',
    text: 'Is multi-factor authentication (MFA) enabled across email and financial systems?',
    section: 'cyber',
    group: 'cyber_crime',
    sort_order: 540,
    field_type: 'boolean',
    required: true,
    field_path: 'cyber.mfa_enabled',
    conditions: CYBER_CONDITION,
    tags: ['cyber', 'lob'],
  },
  {
    id: 'cyb_edr',
    text: 'Is an EDR / endpoint security solution in place?',
    section: 'cyber',
    group: 'cyber_crime',
    sort_order: 550,
    field_type: 'boolean',
    required: true,
    field_path: 'cyber.edr',
    conditions: CYBER_CONDITION,
    tags: ['cyber', 'lob'],
  },
  {
    id: 'cyb_records',
    text: 'Approximately how many resident PII records are stored?',
    section: 'cyber',
    group: 'cyber_crime',
    sort_order: 560,
    field_type: 'number',
    placeholder: '2500',
    required: true,
    field_path: 'cyber.pii_records',
    conditions: CYBER_CONDITION,
    tags: ['cyber', 'lob'],
  },
  {
    id: 'cyb_incidents',
    text: 'Any cyber incidents in the last 5 years?',
    subtext: 'Ransomware, business email compromise, phishing losses.',
    section: 'cyber',
    group: 'cyber_crime',
    sort_order: 570,
    field_type: 'boolean',
    required: true,
    field_path: 'cyber.prior_incidents',
    conditions: CYBER_CONDITION,
    tags: ['cyber', 'lob'],
  },
  {
    id: 'cyb_incidents_detail',
    text: 'Describe the incidents',
    section: 'cyber',
    group: 'cyber_crime',
    sort_order: 580,
    field_type: 'textarea',
    required: true,
    field_path: 'cyber.prior_incidents_detail',
    conditions: [...CYBER_CONDITION, { field: 'cyb_incidents', operator: 'eq', value: true }],
    tags: ['cyber', 'lob'],
  },
  {
    id: 'cyb_cash_limits',
    text: 'On-site cash handling limits',
    subtext: 'Petty cash and daily/weekly cash collection caps, if any.',
    section: 'cyber',
    group: 'cyber_crime',
    sort_order: 590,
    field_type: 'text',
    placeholder: '$200 petty cash; cash rent deposited daily',
    required: false,
    field_path: 'cyber.cash_limits',
    conditions: CYBER_CONDITION,
    tags: ['cyber', 'lob'],
  },
  {
    id: 'cyb_dishonesty',
    text: 'Any prior employee dishonesty / fidelity claims?',
    section: 'cyber',
    group: 'cyber_crime',
    sort_order: 600,
    field_type: 'boolean',
    required: true,
    field_path: 'cyber.dishonesty_claims',
    conditions: CYBER_CONDITION,
    tags: ['cyber', 'lob'],
  },
  {
    id: 'cyb_wire_authority',
    text: 'Wire transfer authority — single signer or dual approval?',
    section: 'cyber',
    group: 'cyber_crime',
    sort_order: 610,
    field_type: 'select',
    options: [
      { label: 'Dual approval required', value: 'dual' },
      { label: 'Single signer', value: 'single' },
      { label: 'No wire transfers', value: 'none' },
    ],
    required: true,
    field_path: 'cyber.wire_authority',
    conditions: CYBER_CONDITION,
    tags: ['cyber', 'lob'],
  },
  {
    id: 'cyb_wire_verification',
    text: 'Are wire requests verified by callback to a known phone number?',
    section: 'cyber',
    group: 'cyber_crime',
    sort_order: 620,
    field_type: 'boolean',
    required: true,
    field_path: 'cyber.wire_verification',
    conditions: [
      ...CYBER_CONDITION,
      { field: 'cyb_wire_authority', operator: 'neq', value: 'none' },
    ],
    tags: ['cyber', 'lob'],
  },
];
